import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, ErrorBox, Loading, PageTitle } from "./ui";
import { fmtDate, inr } from "../lib/format";

export default function Products() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/membership/bundles"),
      api.get("/investments/catalog"),
      api.get("/investments/portfolio"),
    ])
      .then(([bundles, catalog, portfolio]) =>
        setData({
          bundles: bundles.data?.bundles || [],
          products: catalog.data?.products || [],
          portfolio: portfolio.data,
        })
      )
      .catch(() => setError("Could not load products. Please refresh."));
  }, []);

  if (error) return <ErrorBox message={error} />;
  if (!data) return <Loading label="Loading products" />;

  const { bundles, products, portfolio } = data;
  const positions = portfolio?.positions || [];

  return (
    <div data-testid="products-page">
      <PageTitle title="Products" sub="Membership bundles and AI-powered investment products from your Winter Protocol account." />

      {positions.length > 0 && (
        <Card className="mt-8 border-[#D4AF37]/30" data-testid="portfolio-card">
          <h2 className="font-display text-lg font-semibold text-white">Your Portfolio</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#636B78] uppercase">Invested</p>
              <p className="mt-1 font-display text-xl font-bold text-white">{inr(portfolio.totals?.total_principal_inr)}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#636B78] uppercase">Current Value</p>
              <p className="mt-1 font-display text-xl font-bold text-gold-gradient">{inr(portfolio.totals?.total_current_value_inr)}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#636B78] uppercase">Accrued Profit</p>
              <p className="mt-1 font-display text-xl font-bold text-emerald-400">{inr(portfolio.totals?.total_accrued_profit_inr)}</p>
            </div>
          </div>
          <div className="mt-5 divide-y divide-white/[0.06]">
            {positions.map((p) => (
              <div key={p.id || p.product_id} className="flex items-center justify-between py-3" data-testid={`portfolio-position-${p.product_id}`}>
                <div>
                  <p className="text-sm font-medium text-white">{p.product_name || p.product_id}</p>
                  <p className="mt-0.5 font-mono text-[11px] text-[#636B78]">
                    since {fmtDate(p.started_at)} · {p.distribution}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm text-white">{inr(p.principal_inr)}</p>
                  <p className="font-mono text-[11px] text-emerald-400">+{inr(p.accrued_profit_inr)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <h2 className="mt-10 font-display text-lg font-semibold text-white">AI-Powered Investment Products</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {products.map((p) => (
          <Card key={p.id} data-testid={`product-card-${p.id}`} className="transition-colors duration-300 hover:border-[#D4AF37]/30">
            <p className="font-display text-base font-semibold text-white">{p.name}</p>
            <p className="mt-1 text-xs text-[#636B78]">{p.subtitle}</p>
            <p className="mt-4 font-display text-2xl font-bold text-gold-gradient">
              {p.apr_low === p.apr_high ? `${p.apr_low}%` : `${p.apr_low}–${p.apr_high}%`}
            </p>
            <p className="mt-1 font-mono text-[10px] tracking-[0.25em] text-[#636B78] uppercase">Estimated APR</p>
            <div className="mt-4 flex items-center justify-between border-t border-white/[0.06] pt-3 text-[11px] text-[#99A1AF]">
              <span>Min {inr(p.min_investment_inr)}</span>
              <span className="font-mono uppercase tracking-wider">{p.distribution}</span>
            </div>
          </Card>
        ))}
      </div>

      <h2 className="mt-10 font-display text-lg font-semibold text-white">Membership Bundles</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {bundles.map((b) => (
          <Card key={b.id} data-testid={`bundle-card-${b.id}`} className={b.highlight ? "border-[#D4AF37]/40" : ""}>
            {b.badge && (
              <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2.5 py-0.5 font-mono text-[9px] tracking-[0.2em] text-[#F3E5AB] uppercase">
                {b.badge}
              </span>
            )}
            <p className="mt-3 font-display text-base font-semibold text-white">{b.name}</p>
            <p className="mt-2 font-display text-2xl font-bold text-white">{inr(b.price_inr)}</p>
            <p className="mt-1 text-xs text-[#636B78]">
              {b.total_win} WIN total <span className="text-[#3A3F4A]">({b.win_credits} + {b.bonus_win} bonus)</span>
            </p>
          </Card>
        ))}
      </div>

      <Card className="mt-8 border-white/[0.08] bg-[#0B0B0F]" data-testid="products-purchase-note">
        <p className="text-sm leading-relaxed text-[#99A1AF]">
          Purchases and investments are processed through Winter Protocol's existing secure payment
          system. To buy a bundle or start an investment, use the{" "}
          <Link to="/#download" className="text-[#D4AF37] hover:text-[#F3E5AB]">Winter Protocol app</Link>{" "}
          — everything stays in sync with this account automatically.
        </p>
        <p className="mt-2 text-[11px] text-[#636B78]">
          APR figures are estimates, not guaranteed returns.
        </p>
      </Card>
    </div>
  );
}
